// src/components/chat/CatylexCitationBubble.tsx

/**
 * CatylexCitationBubble
 *
 * Inline chip for catylex citations. Same neutral chip style as
 * DocumentCitationBubble / PlaybookCitationBubble for visual consistency;
 * the teal magnifying-glass icon distinguishes it from playbook and uploaded
 * document citations. Slim payload comes from
 * src/tools/catylex_search/citation.py.
 *
 * With `groupRefs` the chip stands for several adjacent Catylex citations:
 * it shows the first one's label plus "+N", pages through all of them in the
 * tooltip, and can be pinned open by click, tap or Enter.
 */

import React, { useCallback, useRef } from "react";
import { useDocumentCitations } from "../../contexts/DocumentCitationContext";
import type { CatylexCitationTooltipData } from "../../contexts/CatylexCitationTooltipContext";
import { useCatylexCitationTooltip } from "../../contexts/CatylexCitationTooltipContext";
import type { CatylexCitation } from "../../interfaces/documentCitationTypes";
import { isCatylexCitation } from "../../interfaces/documentCitationTypes";
import { CatylexIcon } from "./DocumentCitationIcons";
import { CitationCountBadge } from "./CitationGroupControls";
import { useCitationGroupChip } from "./hooks/useCitationGroupChip";

export interface CatylexCitationBubbleProps {
  /** Reference matching the <doc-cite data-ref="REF" /> tag */
  citationRef: string;
  /** Refs of a merged run of Catylex citations, `citationRef` first. Absent for a single citation. */
  groupRefs?: string[];
}

const labelOf = (citation: CatylexCitation): string =>
  citation.breadcrumb.length > 0
    ? citation.breadcrumb[citation.breadcrumb.length - 1]
    : citation.document_title;

const toTooltipData = (citation: CatylexCitation): CatylexCitationTooltipData => ({
  documentTitle: citation.document_title,
  chunkType: citation.chunk_type,
  breadcrumb: citation.breadcrumb,
  citedText: citation.cited_text,
  atrUrl: citation.atr_url,
  contractStatus: citation.contract_status,
  pages: citation.pages,
  concepts: citation.concepts,
});

export const CatylexCitationBubble: React.FC<CatylexCitationBubbleProps> = ({
  citationRef,
  groupRefs,
}) => {
  const { lookupCitation } = useDocumentCitations();
  const { state, showTooltip, hideTooltip, showTooltipGroup, pageTooltip, closeTooltip } =
    useCatylexCitationTooltip();
  const bubbleRef = useRef<HTMLSpanElement>(null);

  const citation = lookupCitation(citationRef);
  const groupCitations = (groupRefs ?? [])
    .map(lookupCitation)
    .filter((c): c is CatylexCitation => !!c && isCatylexCitation(c));
  const isGroup = groupCitations.length > 1;
  const isOpen = state.isVisible && !!state.group && state.group.anchorEl === bubbleRef.current;

  const openGroup = useCallback(
    (pinned: boolean) => {
      if (!bubbleRef.current) return;
      showTooltipGroup(bubbleRef.current, groupCitations.map(toTooltipData), pinned);
    },
    [showTooltipGroup, groupCitations]
  );

  const groupChipProps = useCitationGroupChip({
    isGroup,
    isOpen,
    isPinned: state.pinned,
    open: openGroup,
    close: closeTooltip,
    page: pageTooltip,
  });

  const handleMouseEnter = useCallback(() => {
    if (isGroup) {
      openGroup(false);

      return;
    }
    const cit = lookupCitation(citationRef);
    if (!cit || !isCatylexCitation(cit)) return;
    const rect = bubbleRef.current?.getBoundingClientRect() ?? null;
    showTooltip({
      anchorRect: rect,
      documentTitle: cit.document_title,
      chunkType: cit.chunk_type,
      breadcrumb: cit.breadcrumb,
      citedText: cit.cited_text,
      atrUrl: cit.atr_url,
      contractStatus: cit.contract_status,
      pages: cit.pages,
      concepts: cit.concepts,
    });
  }, [isGroup, openGroup, showTooltip, lookupCitation, citationRef]);

  const handleMouseLeave = useCallback(() => {
    hideTooltip();
  }, [hideTooltip]);

  // No payload for this ref: render nothing rather than invent a source label
  if (!citation) return null;
  const label =
    isCatylexCitation(citation) && citation.breadcrumb.length > 0
      ? citation.breadcrumb[citation.breadcrumb.length - 1]
      : citation.document_title;
  const ariaLabel = isGroup
    ? `${groupCitations.length} Catylex citations: ${groupCitations.map(labelOf).join(", ")}`
    : `Catylex citation: ${label}`;

  return (
    <span
      ref={bubbleRef}
      className="catylex-citation-bubble"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...groupChipProps}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "2px 8px",
        margin: "0 2px",
        backgroundColor: "#F5F5F5",
        border: "1px solid #E0E0E0",
        borderRadius: "6px",
        fontSize: "11px",
        fontWeight: 500,
        color: "#424242",
        cursor: "pointer",
        transition: "all 0.15s ease",
        whiteSpace: "nowrap",
        verticalAlign: "baseline",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.backgroundColor = "#EEEEEE";
        e.currentTarget.style.borderColor = "#90CAF9";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.backgroundColor = "#F5F5F5";
        e.currentTarget.style.borderColor = "#E0E0E0";
      }}
    >
      <CatylexIcon size={14} color="#9E9E9E" />
      <span
        style={{
          maxWidth: "160px",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </span>
      {isGroup && <CitationCountBadge count={groupCitations.length - 1} />}
    </span>
  );
};

export default CatylexCitationBubble;
